export default customElements.define('w-carousel', class Carousel extends wealthify.CustomElementBaseClass {

    constructor () {
        const styleSelector = 'c-carousel';
        const config = {
            styleSelector: styleSelector,
            templateHTML: `
                <div class="${styleSelector}__container" data-root>
                    <div class="${styleSelector}__tabbuttonlist u-display__none" data-tabbuttonlist></div>
                    <div class="${styleSelector}__controls">
                        <w-widget-button class="${styleSelector}__navigation-button ${styleSelector}__navigation-button--inline" icon="caret-back" data-back-button-inline></w-widget-button>
                        <div class="${styleSelector}__content" data-content>
                            <ul class="${styleSelector}__track" data-track>
                                <span class="${styleSelector}__item" data-slot="item" data-slot-required data-slot-multiple data-item></span>
                            </ul>
                        </div>
                       <w-widget-button class="${styleSelector}__navigation-button ${styleSelector}__navigation-button--inline" icon="caret-forward" data-forward-button-inline></w-widget-button>
                    </div>
                    <div class="${styleSelector}__countdown u-display__hidden" data-countdown></div>
                    <div class="${styleSelector}__timeline-container">
                        <w-widget-button class="${styleSelector}__navigation-button" icon="caret-back" data-back-button-below></w-widget-button>
                        <div class="${styleSelector}__numbered-timeline" data-numbered-timeline><span data-current-slide-index></span>/<span data-total-slides-count></span></div>
                        <ul class="${styleSelector}__timeline-button-list" data-timelinebuttonlist>
                        </ul>
                        <w-widget-button class="${styleSelector}__navigation-button" icon="caret-forward" data-forward-button-below></w-widget-button>
                    </div>
                    <div aria-live="polite" aria-atomic="true" class="u-display__hidden" data-liveregion></div>
                    <template data-tabtemplate>
                        <w-button small="true" type="secondary" class="${styleSelector}__tabbutton"></w-button>
                    </template>
                </div>`,
            hasChildren: true,
            cacheDom: ['data-root', 'data-tabbuttonlist', 'data-content', 'data-track', 'data-back-button-inline', 'data-forward-button-inline', 
                'data-back-button-below', 'data-forward-button-below', 'data-item', 'data-countdown', 'data-timelinebuttonlist', 'data-liveregion', 'data-tabtemplate', 'data-current-slide-index', 'data-total-slides-count', 'data-numbered-timeline'],
            defaultSlotContents: {}
        };

        super(config);

        this.swipeMinimumThreshold = 150;
        this.maxSlidesForDottedTimeline = 5;

        this.currentItemWidthPercent = 100;
        this.startX;
        this.endX;
        this.lastSelected = null;
        this.nextSelected = null;
        this.scrollTick = 0;
        this.interval = [];
        this.labels = [];

        this.dragging = false;
        this.moved = false;
        this.initialised = false;
        this.focused = false;
        this.userNavigated = false;
        this.stopped = false;

        this.onConnectedCallback = () => {
            this.classList.add(this.styleSelector);

            this._validateItems();
            this._initialiseTrackAndButtons();
            this._setSelected(true);
            this._initialiseNumberedTimeline();

            this.addEventListener('mouseover', () => this._pauseScroll());
            this.addEventListener('mouseleave', () => this._resumeScroll());

            this.addEventListener('focus', () => {
                this.focused = true;
                this._pauseScroll();
            }, true);
            this.addEventListener('blur', () => {
                this.focused = false;
                this._resumeScroll();
            }, true);

            this.dom.track.addEventListener('mousedown', this._dragStart.bind(this));
            this.dom.track.addEventListener('dragstart', this._dragEnd.bind(this));
            document.addEventListener('mousemove', this._drag.bind(this));
            document.addEventListener('mouseup', this._dragEnd.bind(this));

            this.dom.track.addEventListener('touchstart', this._dragStart.bind(this));
            document.addEventListener('touchmove', this._drag.bind(this));
            document.addEventListener('touchend', this._dragEnd.bind(this));            
        };
    }

    static scrollButtonsPositionEnum = {
        Inline: 'inline',
        Below: 'below',
        None: 'none'
    };

    static get validScrollButtonsPositions() {
        return [...Object.values(this.scrollButtonsPositionEnum)];
    }

    static defaultScrollButtonsPosition = this.scrollButtonsPositionEnum.Inline;

    static get observedProps() {
        return [
            new wealthify.Prop('autoscroll', {
                default: false,
                type: wealthify.propTypes.Boolean()
            }),
            new wealthify.Prop('hidecountdown', {
                default: false,
                type: wealthify.propTypes.Boolean()
            }),
            new wealthify.Prop('label', {required: true}),
            new wealthify.Prop('scrollspeed', {
                default: 10,
                type: wealthify.propTypes.Number()
            }),
            new wealthify.Prop('selected', {
                default: 0,
                nullable: true,
                type: wealthify.propTypes.Number()
            }),
            new wealthify.Prop('hidetimeline', {
                default: false,
                type: wealthify.propTypes.Boolean()
            }),
            new wealthify.Prop('scroll-buttons-position', {
                default: this.defaultScrollButtonsPosition,
                type: wealthify.propTypes.ArrayItem(this.validScrollButtonsPositions)
            }),
            new wealthify.Prop('showtabs', {
                default: false,
                type: wealthify.propTypes.Boolean()
            }),
            new wealthify.Prop('inverted', {
                default: false,
                type: wealthify.propTypes.Boolean()
            })
        ];
    }

    get propCallbacks() {
        return {
            autoscroll: () => {
                const classListAction = this.autoscroll ? 'remove' : 'add';

                if (this.autoscroll) {
                    this._startScroll();
                } else {
                    this._stopScroll();
                }

                if (this.dom.timelinebutton) {
                    this._addScrollControlButton();
                }

                this.dom.countdown.classList[classListAction]('u-display__hidden');
                this._toggleTimeline();
            },
            hidecountdown: () => {
                const classListAction = this.hidecountdown ? 'add' : 'remove';

                this.dom.countdown.classList[classListAction]('u-display__none');
            },
            label: () => this.dom.root.setAttribute('aria-label', this.label),
            scrollspeed: () => {
                if (this.isComponentReady) {
                    this._restartScroll();
                }
            },
            selected: () => this._setSelected(),
            hidetimeline: () => this._toggleTimeline(),
            scrollButtonsPosition: () => this._setControlsPosition(),
            showtabs: () => {
                const tabsClassListAction = this.showtabs ? 'remove' : 'add';

                this.dom.tabbuttonlist.classList[tabsClassListAction]('u-display__none');

                this._toggleTimeline();
            },
            inverted: () => {
                const classListAction = this.inverted ? 'add' : 'remove';

                this.dom.root.classList[classListAction](`${this.styleSelector}--inverted`);
            }
        };
    }

    get nextSlide () {
        return this.selected + 1 > this.bounds.max ? 0 : this.selected + 1;
    }

    get previousSlide () {
        return this.selected - 1 < 0 ? this.bounds.max  : this.selected - 1;
    }

    get selectedItem () {
        // If items are not in array, then only the single item can be selected
        return Array.isArray(this.dom.item) ? this.dom.item[this.selected] : this.dom.item;
    }

    get bounds () {
        return {
            min: 0,
            max: wealthify.type.array.isArray(this.dom.item) ? this.dom.item.length - 1 : 0
        };
    }

    scrollNextSlideReference = (() => {
        this.userNavigated = true;
        this.selected = this.nextSlide;
    }).bind(this);

    scrollPreviousSlideReference = (() => {
        this.userNavigated = true;
        this.selected = this.previousSlide;
    }).bind(this);

    _bindScrollButtons () {
        const handlerAction = this.hidescrollbuttons ? 'removeEventListener' : 'addEventListener';

        this.dom.forwardButtonInline[handlerAction]('click', this.scrollNextSlideReference);
        this.dom.backButtonInline[handlerAction]('click', this.scrollPreviousSlideReference);
        this.dom.forwardButtonBelow[handlerAction]('click', this.scrollNextSlideReference);
        this.dom.backButtonBelow[handlerAction]('click', this.scrollPreviousSlideReference);

    }

    _validateItems () {
        const items = wealthify.type.array.isArray(this.dom.item) ? this.dom.item : [this.dom.item];
        const valid = items.every((item) => item.tagName.toLowerCase() === 'li' && item.getAttribute('label') !== null);

        if (!valid) {
            wealthify.error.throwError(this.tagName, '"item" slots passed to carousel must be <li> HTML elements and must have a label attribute.');
        }
    }

    _initialiseTrackAndButtons () {
        const timelineFragment = document.createDocumentFragment();
        const tabFragment = document.createDocumentFragment();
        const items = wealthify.type.array.isArray(this.dom.item) ? this.dom.item : [this.dom.item];

        this.dom.timelinebutton = [];
        this.dom.tabbutton = [];

        items.forEach((item, index) => {
            const timelineButtonElement = document.createElement('button');
            const tabButtonElement = this.dom.tabtemplate.content.firstElementChild.cloneNode(true);
            const timelineLiElement = document.createElement('li');
            const slideLabel = item.getAttribute('label');
            const ariaLabel =  `Go to ${slideLabel}, item ${index + 1} of ${this.dom.item.length} in ${this.label}`;

            item.style.width = `${100 / (this.dom.item.length ?? 1)}%`;

            timelineLiElement.classList.add(`${this.styleSelector}__timeline-button-item`);

            timelineButtonElement.classList.add(`${this.styleSelector}__timeline-button`);
            timelineButtonElement.setAttribute('type', 'button');
            timelineButtonElement.setAttribute('aria-label', ariaLabel);
            tabButtonElement.setAttribute('aria-label', ariaLabel);
            tabButtonElement.label = slideLabel;

            timelineButtonElement.onclick = tabButtonElement.onclickprop = () => {
                this.userNavigated = true;
                this.selected = null;
                this.selected = index;

                this._restartScroll();
            };

            this.dom.timelinebutton.push(timelineButtonElement);
            this.dom.tabbutton.push(tabButtonElement);
            this.labels.push(ariaLabel);

            timelineLiElement.appendChild(timelineButtonElement);
            timelineFragment.appendChild(timelineLiElement);
            tabFragment.appendChild(tabButtonElement);
        });

        this.dom.timelinebuttonlist.appendChild(timelineFragment);
        this.dom.tabbuttonlist.appendChild(tabFragment);

        if (this.autoscroll) {
            this._addScrollControlButton();
        }

        this._toggleTimeline();

        this.initialised = true;
    }

    _addScrollControlButton () {
        const buttonElement = document.createElement('button');
        const liElement = document.createElement('li');
        const iconComponent = document.createElement('w-icon');

        buttonElement.classList.add(`${this.styleSelector}__control-scroll-button`);

        buttonElement.setAttribute('type', 'button');
        buttonElement.setAttribute('aria-label', 'Stop autoscroll');
        buttonElement.setAttribute('data-scrolling', 'true');

        buttonElement.onclick = () => {
            const isScrolling = this.dom.scrollcontrolbutton.getAttribute('data-scrolling') === 'true';
            const label = isScrolling ? 'Start autoscroll' : 'Stop autoscroll';

            if (isScrolling) {
                this._stopScroll();
                this.dom.scrollcontrolbuttonicon.type = 'play';
            } else {
                this._startScroll();
                this.dom.scrollcontrolbuttonicon.type = 'stop';
            }

            this.dom.scrollcontrolbutton.setAttribute('data-scrolling', !isScrolling);
            this.dom.scrollcontrolbutton.setAttribute('aria-label', label);
            this.stopped = isScrolling;
        };

        iconComponent.type = 'stop';

        this.dom.scrollcontrolbutton = buttonElement;
        this.dom.scrollcontrolbuttonicon = iconComponent;

        buttonElement.appendChild(iconComponent);
        liElement.appendChild(buttonElement);
        this.dom.timelinebuttonlist.appendChild(liElement);
    }

    _setSelected (isFirstTime = false) {
        if (this.selected === null || !this.initialised) {
            return;
        }
        const message = this.labels[this.selected].split('Go to ')[1];

        if (this.lastSelected !== null) {
            this.dom.timelinebutton[this.lastSelected].classList.remove(`${this.styleSelector}__timeline-button--selected`);
            this.dom.tabbutton[this.lastSelected].disabledprop = false;
            this.dom.timelinebutton[this.lastSelected].removeAttribute('disabled');
        }

        this.dom.timelinebutton[this.selected].classList.add(`${this.styleSelector}__timeline-button--selected`);
        this.dom.tabbutton[this.selected].disabledprop = true;
        this.dom.tabbutton[this.selected].selected = true;
        this.dom.timelinebutton[this.selected].disabled = true;
        this.dom.track.style.width = `${this.dom.item.length * 100}%`;
        this.dom.track.style.left = `-${this.currentItemWidthPercent * this.selected}%`;
        this.dom.liveregion.textContent = message;
        this.dom.currentSlideIndex.textContent = this.selected + 1;
        this.lastSelected = this.selected;

        if (!isFirstTime) {
            this._setHeight();
        } else {
            setTimeout(this._setHeight.bind(this));
        }

        if (this.userNavigated) {
            this._switchFocus();
        }

        this.userNavigated = false;

        this._addPaddingToSurroundingItems();
    }

    _setHeight () {
        const isNotRendered = this.offsetParent === null;

        if (isNotRendered) {
            // If carousel not loaded
            // Calculate height once node is visible
            wealthify.dom.runOnNodeVisible(this, this._setHeightByFirstItem);
        } else {
            if (document.readyState === 'complete') {
                this._setHeightByFirstItem();
            } else {
                // Calculation of carousel first item height can be affected by fonts not being loaded
                // Run on font loading complete
                document.fonts.onloadingdone = () => this._setHeightByFirstItem();
            }
        }
    }

    _setHeightByFirstItem (component = this) {
        component.dom.track.style.height = `${component.selectedItem.firstElementChild.getBoundingClientRect().height}px`;
    }

    _toggleTimeline () {
        if (!this.dom.timelinebutton) {
            return;
        }

        const timelineListClassListAction = !this.autoscroll && this.hidetimeline ? 'add' : 'remove';
        const timelineButtonClassListAction = this.autoscroll && this.hidetimeline ? 'add' : 'remove';
        const timelineItems = this.dom.timelinebuttonlist.children;

        // Toggle all timeline items apart from play pause control
        Array.from(timelineItems).forEach((timelineButton, index) => {
            const isNotPlayPauseButton = index !== (timelineItems.length - 1);

            if (isNotPlayPauseButton) {
                timelineButton.classList[timelineButtonClassListAction]('u-display__none');
            }
        });

        this.dom.timelinebuttonlist.classList[timelineListClassListAction]('u-display__none');
    }

    _setControlsPosition() {
        const reducedClassListAction = this.scrollButtonsPosition === this.constructor.scrollButtonsPositionEnum.Inline ? 'add' : 'remove';
        const inlineScrollButtonsClassListAction = this.scrollButtonsPosition === this.constructor.scrollButtonsPositionEnum.Inline ? 'remove' : 'add';
        const belowScrollButtonsClassListAction = this.scrollButtonsPosition === this.constructor.scrollButtonsPositionEnum.Below ? 'remove' : 'add';

        this.dom.content.classList[reducedClassListAction](`${this.styleSelector}--with-inline-scroll-buttons`);
        
        // inline buttons
        this.dom.backButtonInline.classList[inlineScrollButtonsClassListAction]('u-display__none');
        this.dom.forwardButtonInline.classList[inlineScrollButtonsClassListAction]('u-display__none');
        
        // below buttons
        this.dom.backButtonBelow.classList[belowScrollButtonsClassListAction]('u-display__none');
        this.dom.forwardButtonBelow.classList[belowScrollButtonsClassListAction]('u-display__none');

        this._bindScrollButtons();
    }

    _initialiseNumberedTimeline () {
        const totalSlides = this.dom.timelinebuttonlist.children.length;
        const numberedTimelineClassAction = totalSlides > this.maxSlidesForDottedTimeline && !this.hidetimeline ? 'remove' : 'add';
        const timelineButtonsClassAction = totalSlides > this.maxSlidesForDottedTimeline || this.hidetimeline ? 'add' : 'remove';

        this.dom.numberedTimeline.classList[numberedTimelineClassAction]('u-display__none');
        this.dom.timelinebuttonlist.classList[timelineButtonsClassAction]('u-display__none');

        this.dom.currentSlideIndex.textContent = this.selected + 1;
        this.dom.totalSlidesCount.textContent = this.dom.timelinebuttonlist.children.length;
    }

    _switchFocus () {
        if (this.lastSelected !== null) {
            this.dom.item[this.lastSelected].removeAttribute('tabindex');
        }

        const transitionDurationMilliseconds = getComputedStyle(this.dom.track).transitionDuration.split('s')[0] * 1000;

        this.selectedItem.setAttribute('tabindex', -1);

        setTimeout(() => this.selectedItem.focus({preventScroll: true}), transitionDurationMilliseconds);
    }

    _addPaddingToSurroundingItems () {
        this.dom.item.forEach((item) => item.style.paddingLeft = item.style.paddingRight = '');

        const surroundingItems = [];
        const isNotFirstItem = this.selected !== 0;
        const isNotLastItem = this.selected !== (this.dom.item.length - 1);

        if (isNotFirstItem) {
            surroundingItems.push(this.dom.item[this.selected - 1]);
        }

        if (isNotLastItem) {
            surroundingItems.push(this.dom.item[this.selected + 1]);
        }

        surroundingItems.forEach((item) => item.style.paddingLeft = item.style.paddingRight = '1rem');
    }

    _dragStart (event) {

        // TODO - Rework with container base functionality - PDB-1011.
        const isInModal = wealthify.dom.getClosestMatchingParent(event.target,'w-modal') !== null;

        if (this.dragging || isInModal) {
            return;
        }

        wealthify.dom.getClosestMatchingParent(event.target,'w-modal');

        this.dragging = true;
        this.startX = this._getClientXPosition(event);
        this.lastSelected = this.selected;
        this.selected = null; // set to null so that slide can be re-selected

        this.previousTransitionDuration = getComputedStyle(this.dom.track).transitionDuration;
        this.dom.track.style.transitionDuration = '0s';

        this._stopScroll();
    }

    _drag (event) {
        const noButtonsPressedOnMouseEvent = event.type !== 'touchmove' && event.which === 0;

        if (!this.dragging || noButtonsPressedOnMouseEvent) {
            return;
        }

        const moveX = this.endX ?? this.startX; // use startX on first move, otherwise use previous endX

        this.endX = this._getClientXPosition(event);

        const shiftX = moveX - this.endX;
        const shiftXPercent = (shiftX / this.dom.root.offsetWidth) * 100;
        const newLeftPercent = `${Number.parseFloat(this.dom.track.style.left) - shiftXPercent}%`;

        this.dom.track.style.left = newLeftPercent;
        this.moved = true;
    }

    _dragEnd() {
        const noMovement = !this.dragging || !this.moved;

        this.dom.track.style.transitionDuration = this.previousTransitionDuration;
        this.dragging = false;
        this.moved = false;

        if (noMovement) {
            this._resetPositionVariables();

            return;
        }

        let moveAmount = this.startX - this.endX;

        // get positive move amount
        moveAmount = moveAmount < 0 ? moveAmount * -1 : moveAmount;

        this._restartScroll();

        if (moveAmount < this.swipeMinimumThreshold) {
            // movement below threshold, return to previous slide
            this._resetPositionVariables();

            return;
        }

        // get next slide derived from start and end position, and whether the cursor moved
        const nextSelected = this.startX < this.endX ? this.lastSelected - 1 : this.lastSelected + 1;

        this.selected = wealthify.type.number.getClampedValue(nextSelected, this.bounds.min, this.bounds.max);
        this._resetPositionVariables();
    }

    _resetPositionVariables () {
        this.selected = this.lastSelected;
        this.startX = null;
        this.endX = null;
    }

    _getClientXPosition(event) {
        if (event.type === 'touchstart' || event.type === 'touchmove') {
            return event.touches[0].clientX - this.dom.root.getBoundingClientRect().left;
        } else {
            return event.clientX - this.dom.root.getBoundingClientRect().left;
        }
    }

    _startScroll () {
        this.scrollTick = this.scrollspeed;
        this.dom.countdown.style.transitionDuration = '1s';

        this._startInterval();
    }

    _restartScroll () {
        this.scrollTick = this.scrollspeed;
        this._resetCountdown();
    }

    _stopScroll () {
        this.scrollTick = 0;
        this._resetCountdown();
        this._stopInterval();
    }

    _pauseScroll () {
        if (this.autoscroll) {
            this._stopInterval();
        }
    }

    _resumeScroll () {
        if (this.focused || this.stopped) {
            return;
        }

        if (this.autoscroll) {
            this._startInterval();
        }
    }

    _startInterval () {
        this._transitionCountdown();

        this.interval.push(setInterval(() => {
            this._progressScrollTick();
        }, 1000));

        if (this.focused) {
            this._pauseScroll();
        }
    }

    _stopInterval () {
        clearInterval(this.interval[0]);
        this.interval.shift();
    }

    _progressScrollTick () {
        if (this.scrollTick === this.scrollspeed) {
            this.dom.countdown.style.transitionDuration = '1s';
        }

        if (this.scrollTick === 0) {
            this.selected = this.nextSlide;
            this.scrollTick = this.scrollspeed;

            this._transitionCountdown();
            this._resetCountdown();

            return;
        }

        --this.scrollTick;
        this._transitionCountdown();
    }

    _transitionCountdown () {
        const widthPercent = (this.scrollspeed - this.scrollTick) * (100 / this.scrollspeed);
        const margin = Number.parseInt(getComputedStyle(this.dom.countdown).marginLeft) * 2;
        const width = `calc(${widthPercent}% - ${margin}px)`;

        this.dom.countdown.style.width = width;
    }

    _resetCountdown () {
        this.dom.countdown.style.transitionDuration = '0s';
        this.dom.countdown.style.width = 0;
    }

});